import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress
} from "@material-ui/core";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

// Importe a instância do Axios configurada no Whaticket
import api from "../../services/api"; 

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;


// Função auxiliar para formatar instâncias de Date no padrão YYYY-MM-DD
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function TarefasPorPeriodo() {
  // Calcula o primeiro dia do mês atual
  const [inicio, setInicio] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return formatDate(firstDay);
  });

  // Calcula o último dia do mês atual
  const [fim, setFim] = useState(() => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return formatDate(lastDay);
  });

  const [tarefas, setTarefas] = useState([]);
  const [loading, setLoading] = useState(false);

  // Função que busca as tarefas no Backend
  const handleFiltrarTarefas = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks-report", {
        params: { inicio, fim }
      });
      setTarefas(data);
    } catch (err) {
      console.error("Erro ao buscar relatório de tarefas:", err);
    } finally {
      setLoading(false);
    }
  };

  // Carrega os dados na primeira renderização
  useEffect(() => {
    handleFiltrarTarefas();
  }, []);

  const handleExportarPDFTarefas = () => {
    try {
      const tarefasOrdenadas = [...tarefas].sort((a, b) => {
        if ((a.analista || "").localeCompare(b.analista || "") !== 0) {
          return (a.analista || "").localeCompare(b.analista || "");
        }
        return (a.cliente || "").localeCompare(b.cliente || "");
      });

      const agrupadoPorAnalista = tarefasOrdenadas.reduce((acc, item) => {
        const analista = item.analista || "Não Informado";
        if (!acc[analista]) {
          acc[analista] = { qtd: 0, total: 0 };
        }
        acc[analista].qtd += 1;
        acc[analista].total += Number(item.valorUnitario || 0);
        return acc;
      }, {});

      const valorGeral = tarefasOrdenadas.reduce(
        (sum, item) => sum + Number(item.valorUnitario || 0),
        0
      );

      const tableBody = [
        [
          { text: "Analista", style: "tableHeader" },
          { text: "Ticket", style: "tableHeader" },
          { text: "Cliente", style: "tableHeader" },
          { text: "Tarefa", style: "tableHeader" },
          { text: "Descrição", style: "tableHeader" },
          { text: "Valor", style: "tableHeader" },
          { text: "Concluído em", style: "tableHeader" }
        ]
      ];

      tarefasOrdenadas.forEach((t) => {
        tableBody.push([
          t.analista || "-",
          `#${t.ticketId}`,
          t.cliente || "-",
          t.tarefa || "-",
          t.descricao || "-",
          `R$ ${Number(t.valorUnitario || 0).toFixed(2)}`,
          t.concludedAt || "-"
        ]);
      });

      const summaryBody = [
        [
          { text: "Analista", style: "tableHeaderSummary" },
          { text: "Qtd. Tarefas", style: "tableHeaderSummary" },
          { text: "Total Faturado", style: "tableHeaderSummary" }
        ]
      ];

      Object.keys(agrupadoPorAnalista).forEach((analista) => {
        summaryBody.push([
          analista,
          agrupadoPorAnalista[analista].qtd.toString(),
          `R$ ${agrupadoPorAnalista[analista].total.toFixed(2)}`
        ]);
      });

      summaryBody.push([
        { text: "TOTAL GERAL", bold: true },
        { text: tarefasOrdenadas.length.toString(), bold: true },
        { text: `R$ ${valorGeral.toFixed(2)}`, bold: true }
      ]);

      const docDefinition = {
        pageSize: "A4",
        pageMargins: [20, 20, 20, 20],
        content: [
          { text: "Relatório de Tarefas Concluídas por Período", style: "header" },
          { text: `Período: ${inicio} até ${fim}`, style: "subheader" },
          {
            table: {
              headerRows: 1,
              widths: ["*", "auto", "*", "*", "*", "auto", "auto"],
              body: tableBody
            },
            layout: {
              fillColor: (rowIndex) => (rowIndex === 0 ? "#2563EB" : null)
            }
          },
          { text: "Resumo e Totalizador por Analista", style: "sectionTitle" },
          {
            table: {
              headerRows: 1,
              widths: ["*", "auto", "auto"],
              body: summaryBody
            },
            layout: {
              fillColor: (rowIndex) => {
                if (rowIndex === 0) return "#1E293B";
                if (rowIndex === summaryBody.length - 1) return "#F1F5F9";
                return null;
              }
            }
          }
        ],
        styles: {
          header: { fontSize: 16, bold: true, margin: [0, 0, 0, 4] },
          subheader: { fontSize: 10, margin: [0, 0, 0, 12], color: "#475569" },
          sectionTitle: { fontSize: 12, bold: true, margin: [0, 16, 0, 6] },
          tableHeader: { bold: true, color: "white", fontSize: 8.5 },
          tableHeaderSummary: { bold: true, color: "white", fontSize: 9 }
        },
        defaultStyle: {
          fontSize: 8
        }
      };

      pdfMake.createPdf(docDefinition).download(`relatorio_tarefas_${inicio}_a_${fim}.pdf`);
    } catch (error) {
      console.error("Erro ao gerar PDF de tarefas:", error);
    }
  };

  const handleAbrirAcessosNovaAba = () => {
    window.open("/acessos-remotos", "_blank");
  };

  return (
    <Container maxWidth="lg" style={{ paddingTop: 20, paddingBottom: 20 }}>
      <Paper style={{ padding: 20 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" color="primary">
            Tarefas Concluídas por Período
          </Typography>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<OpenInNewIcon />}
            onClick={handleAbrirAcessosNovaAba}
          >
            Abrir Gerenciador de Acessos Remotos
          </Button>
        </Box>

        <Box display="flex" gap={2} alignItems="center" my={2} flexWrap="wrap">
          <TextField
            label="Data Início"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={inicio}
            onChange={(e) => setInicio(e.target.value)}
          />
          <TextField
            label="Data Fim"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={fim}
            onChange={(e) => setFim(e.target.value)}
          />
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleFiltrarTarefas}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Filtrar"}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportarPDFTarefas}
            disabled={tarefas.length === 0 || loading}
          >
            Exportar Tarefas PDF
          </Button>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><b>Analista</b></TableCell>
              <TableCell><b>Ticket</b></TableCell>
              <TableCell><b>Cliente</b></TableCell>
              <TableCell><b>Tarefa</b></TableCell>
              <TableCell><b>Descrição</b></TableCell>
              <TableCell><b>Valor</b></TableCell>
              <TableCell><b>Concluído em</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Carregando dados...
                </TableCell>
              </TableRow>
            ) : tarefas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhuma tarefa encontrada.
                </TableCell>
              </TableRow>
            ) : (
              tarefas.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.analista || "-"}</TableCell>
                  <TableCell>#{row.ticketId}</TableCell>
                  <TableCell>{row.cliente || "-"}</TableCell>
                  <TableCell>{row.tarefa || "-"}</TableCell>
                  <TableCell>{row.descricao || "-"}</TableCell>
                  <TableCell>R$ {Number(row.valorUnitario || 0).toFixed(2)}</TableCell>
                  <TableCell>{row.concludedAt || "-"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
}